import { useMemo, useState, type ReactNode } from "react";
import { clsx } from "clsx";
import { AlertTriangle, ChevronDown, ChevronUp, Info, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { Tooltip } from "@/components/atoms/Tooltip";
import { InfoHint } from "@/components/atoms/InfoHint";
import { WeekdayPicker } from "@/modules/availability/WeekdayPicker";
import { TimeWindowSlider } from "@/modules/availability/preview/TimeWindowSlider";
import { DiaryTimeField } from "@/modules/diary/DiaryDateTimeField";
import {
  RECOMMENDED_SLOT_SETTINGS,
  TIMEZONE_OPTIONS,
  type AvailabilityUserRef,
  type SlotSettings,
  type SlotSettingsAssignment,
} from "@/types/availability";
import type { UserSummary } from "@/types/entities";
import { formatLocalDate } from "@/utils/date-helpers";
import {
  buildUserSelectOptions,
  userDisplayLabel,
  userIdentityKey,
} from "./searchHelpers";

interface Props {
  value: SlotSettings;
  onChange: (next: SlotSettings) => void;
  onSave?: () => void;
  saving?: boolean;
  /** Negotiators selectable as default users. */
  users?: UserSummary[];
  usersLoading?: boolean;
  /**
   * Notified as the "Default users" search box is typed in. The parent only
   * fetches the first page of negotiators for the scope, so anyone outside it
   * (or past that page) has to be looked up on the server — the owner uses this
   * to run that search and feed the results back through `users`.
   */
  onUserSearchChange?: (query: string) => void;
  saveDisabled?: boolean;
  /** When false the "Default users" list may be left empty (no required marker). */
  usersRequired?: boolean;
  /** Renders every control read-only and greyed out. */
  disabled?: boolean;
  /**
   * Adds the drag-to-set window slider alongside the time fields. Opt-in: the
   * property drawer renders this panel in a narrow column where the slider has
   * nowhere to go, so only the settings page turns it on.
   */
  interactiveWindow?: boolean;
  /**
   * Splits the form into labelled Timing / Window / Team groups with preset
   * shortcuts, for the settings page's scrolling pane. Off by default so the
   * property drawer keeps the compact single-column form it was built around.
   */
  grouped?: boolean;
  /**
   * The scope being edited already has its own availability, so the default
   * window below will never be read. Drops the required markers and swaps the
   * "no window set" warning for a note explaining why it does not matter.
   */
  windowOptional?: boolean;
}

const MAX_HORIZON_DAYS = 30;

// DiaryTimeField speaks the `datetime-local` contract (it preserves the date
// half). The window bounds are plain `HH:mm`, so pin the date to a constant on
// the way in and strip it back off on the way out.
const TIME_DATE_ANCHOR = "2000-01-01";
const toLocalTime = (hhmm: string) => `${TIME_DATE_ANCHOR}T${hhmm || "00:00"}`;
const fromLocalTime = (local: string) => /T(\d{2}:\d{2})/.exec(local)?.[1] ?? null;

/** Which labelled group a numeric field belongs to in the grouped layout. */
type FieldGroupId = "timing" | "window";

const NUMERIC_FIELDS: {
  key: keyof SlotSettings;
  label: string;
  help: string;
  /** Fuller explanation shown on hover; `help` is the one-line version. */
  tooltip: string;
  max?: number;
  /** Left blank-able; everything else must carry a value before saving. */
  optional?: boolean;
  group: FieldGroupId;
}[] = [
  {
    key: "slotDuration",
    label: "Slot duration (min)",
    help: "Length of a viewing",
    tooltip: "How long one viewing appointment lasts, in minutes.",
    group: "timing",
  },
  {
    key: "slotInterval",
    label: "Slot interval (min)",
    help: "Step between slot starts — 0 for back-to-back",
    tooltip:
      "Minutes between one slot's start and the next. Leave it at 0 for back-to-back viewings (each starts as the previous one ends), or set a larger step to space them out.",
    group: "timing",
  },
  {
    key: "topupTravelTime",
    label: "Top-up time (min)",
    help: "Lead-in before the first slot of a block",
    tooltip:
      "Buffer added before the first slot of a block, so the agent has time to get to the property before the first viewing.",
    group: "timing",
  },
  {
    key: "leadsPerSlot",
    label: "Leads per slot",
    help: "Max contacts per slot before it is full",
    tooltip:
      "How many contacts can book the same slot. Above 1 it becomes a group viewing; the slot stops being offered once it's full.",
    group: "timing",
  },
  {
    key: "generationHorizonDays",
    label: "Horizon (days)",
    help: `How far ahead recurring availability is generated (max ${MAX_HORIZON_DAYS} days)`,
    tooltip: `How many days ahead recurring availability is generated, up to ${MAX_HORIZON_DAYS}. Leave blank for the server default.`,
    max: MAX_HORIZON_DAYS,
    optional: true,
    group: "window",
  },
  {
    key: "minNoticeHours",
    label: "Minimum notice (hours)",
    help: "How far in advance a slot must be booked",
    tooltip:
      "Slots starting sooner than this are not offered, so there is always time to prepare. Leave blank for no minimum.",
    optional: true,
    group: "window",
  },
];

/**
 * The starting point for a form with nothing in it — an empty form is the
 * slowest state on the page, and this is a complete, valid rule set the
 * operator can then adjust.
 *
 * It fills every rule, not just the timing four: the point of the button is to
 * leave nothing else to answer, so the window, horizon, notice and timezone
 * come with it. Only the default users are left alone — nobody can guess who
 * takes the viewings.
 */
const DEFAULT_PRESET = {
  label: "Use recommended defaults",
  summary:
    "15 min viewings back-to-back · 1 lead · Mon–Fri 10:00–17:00 · 14 days ahead · 24h notice",
  values: RECOMMENDED_SLOT_SETTINGS,
};

/** Red asterisk sitting at the top-right of a mandatory field's label. */
function RequiredMark() {
  return (
    <span aria-hidden className="align-super text-[10px] leading-none text-red-500">
      *
    </span>
  );
}

/**
 * A labelled block in the grouped layout. When `on` is false it renders its
 * children bare, so a single JSX tree serves both layouts — the drawer sees
 * exactly the markup it always did.
 */
function FieldGroup({
  on,
  id,
  title,
  description,
  children,
}: {
  on: boolean;
  id?: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  if (!on) return <>{children}</>;
  return (
    <section id={id} className="border-b border-bridge-border/60 px-7 py-6 last:border-b-0">
      <h4 className="font-mono text-[11.5px] font-bold uppercase tracking-[.14em] text-bridge-muted">
        {title}
      </h4>
      <p className="mb-4 mt-1 text-[13px] text-bridge-muted">{description}</p>
      {children}
    </section>
  );
}

/**
 * Per-property slot rules form (booking-service-architecture.md §3.2). Controlled —
 * persistence is the parent's job.
 */
export function AvailabilitySettingsPanel({
  value,
  onChange,
  onSave,
  saving,
  users = [],
  usersLoading,
  onUserSearchChange,
  saveDisabled,
  usersRequired = true,
  disabled = false,
  interactiveWindow = false,
  grouped = false,
  windowOptional = false,
}: Props) {
  const defaultUsers = value.users ?? [];

  // Assignments the roster job switched off. `users` carries only the live ones, so without this
  // they would simply be absent from the screen with no explanation.
  const inactiveAssignments = (value.assignments ?? []).filter(
    (a) => a.activeFlag === false && !defaultUsers.some((u) => u.id === a.userId),
  );
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // The auto-provisioned window only exists once the recurrence is pinned down:
  // Daily, or Weekly with at least one day. Anything short of that is a warning,
  // not an error — the settings still save without a window.
  const windowConfigured =
    value.defaultRecurrenceType === "DAILY" ||
    (value.defaultRecurrenceType === "WEEKLY" && (value.defaultRecurrenceDays?.length ?? 0) > 0);

  // Text inputs that only accept digits — an empty field stays empty (null)
  // instead of snapping back to 0.
  const setNumber = (key: keyof SlotSettings, raw: string, max?: number) => {
    const digits = raw.replace(/\D/g, "");
    if (digits === "") {
      onChange({ ...value, [key]: null });
      return;
    }
    let next = Number(digits);
    if (max != null) next = Math.min(next, max);
    onChange({ ...value, [key]: next });
  };

  // Arrow up/down step the value like a native number input (empty steps from 0).
  const stepNumber = (
    e: React.KeyboardEvent<HTMLInputElement>,
    key: keyof SlotSettings,
    max?: number,
  ) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const current = (value[key] as number | null) ?? 0;
    let next = current + (e.key === "ArrowUp" ? 1 : -1);
    next = Math.max(0, next);
    if (max != null) next = Math.min(next, max);
    onChange({ ...value, [key]: next });
  };

  const userOptions = useMemo(() => {
    const assignedKeys = defaultUsers
      .map((ref) => users.find((u) => u.id === ref.id))
      .filter((u): u is UserSummary => Boolean(u))
      .map(userIdentityKey);
    return buildUserSelectOptions(users, {
      searchQuery: userSearchQuery,
      excludeUserIds: defaultUsers.map((u) => u.id),
      excludeIdentityKeys: assignedKeys,
    });
  }, [users, defaultUsers, userSearchQuery]);

  const labelFor = (ref: AvailabilityUserRef) => {
    const found = users.find((u) => u.id === ref.id);
    // The ref carries its own name/email, so a user who has dropped out of the
    // loaded list still reads as a person rather than a bare uuid.
    return found ? userDisplayLabel(found) : (ref.name || ref.email || ref.id);
  };

  function addUser(id: string) {
    const found = users.find((u) => u.id === id);
    if (!found) return;
    const ref: AvailabilityUserRef = {
      id: found.id,
      sourceCrm: found.sourceCrm,
      externalId: found.externalId,
      name: found.name ?? undefined,
      email: found.email ?? undefined,
    };
    onChange({ ...value, users: [...defaultUsers, ref] });
  }
  function removeUser(id: string) {
    onChange({ ...value, users: defaultUsers.filter((u) => u.id !== id) });
  };

  /** Put a roster-dropped negotiator back on the live list; saving makes it stick. */
  const restoreUser = (a: SlotSettingsAssignment) => {
    if (defaultUsers.some((u) => u.id === a.userId)) return;
    onChange({
      ...value,
      users: [
        ...defaultUsers,
        {
          id: a.userId,
          name: a.name ?? undefined,
          // The stored assignment keeps the CRM identifiers; carry them back so the save writes the
          // same person rather than a partially-identified duplicate.
          sourceCrm: a.sourceCrm ?? "",
          externalId: a.externalId ?? "",
        },
      ],
    });
  }
  function move(idx: number, delta: number) {
    const next = [...defaultUsers];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ ...value, users: next });
  }

  // One label treatment for the whole form: bigger and darker in the grouped
  // settings layout, unchanged in the drawer's compact one.
  const labelClass = clsx(
    "mb-2 flex items-center gap-1",
    grouped ? "text-[13.5px] font-semibold text-bridge-fg" : "text-xs font-medium text-bridge-muted",
  );
  const hintClass = clsx("mt-2 text-bridge-subtle", grouped ? "text-[12px]" : "text-[11px]");

  /**
   * One numeric field. The anchor lets the preview's "Fix" links jump to it.
   *
   * Grouped, the label and its help sit to the left of a fixed-width input.
   * These are all one- or two-digit numbers; stretching the control to fill a
   * 450px column made every field look half-empty and pushed the eye across
   * dead space to reach a value that occupies 20px. Sizing the input to its
   * content is what makes the column read as balanced rather than sparse.
   */
  const numericField = (f: (typeof NUMERIC_FIELDS)[number]) => (
    <div key={f.key} data-preview-anchor={f.key}>
      <label className={labelClass}>
        <span>
          {f.label}
          {!f.optional && <RequiredMark />}
        </span>
        <InfoHint content={f.tooltip} />
      </label>
      <Input
        type="text"
        inputMode="numeric"
        className={grouped ? "h-11 text-[14px] hover:border-bridge-accent/40" : undefined}
        value={(value[f.key] as number | null) ?? ""}
        onChange={(e) => setNumber(f.key, e.target.value, f.max)}
        onKeyDown={(e) => stepNumber(e, f.key, f.max)}
        disabled={disabled}
      />
      <p className={hintClass}>{f.help}</p>
    </div>
  );

  const timezoneField = (
    <div>
      <label
        className={labelClass}
      >
        <span>
          Timezone
          <RequiredMark />
        </span>
        <InfoHint content="Timezone the window times and generated slots are interpreted in." />
      </label>
      <MenuSelect
        hideLabel
        value={value.timezone}
        onChange={(tz) => onChange({ ...value, timezone: tz })}
        options={TIMEZONE_OPTIONS}
        searchable
        disabled={disabled}
        placeholder="Select timezone"
      />
    </div>
  );

  // In the flat layout every numeric field shares one grid; grouped, the timing
  // four sit above and the horizon/notice pair travels with the window.
  const timingFields = NUMERIC_FIELDS.filter((f) => grouped ? f.group === "timing" : true);
  const windowNumericFields = grouped ? NUMERIC_FIELDS.filter((f) => f.group === "window") : [];

  return (
    <div className={clsx(!grouped && "space-y-4", disabled && "opacity-60")}>
      <FieldGroup
        on={grouped}
        title="Start from"
        description="A complete, valid set of rules you can then adjust."
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            onChange({
              ...value,
              ...DEFAULT_PRESET.values,
              // Whoever is already on the list stays on it — the preset has
              // nothing to say about which negotiators take the viewings.
              users: value.users,
            })
          }
          className="w-full max-w-md rounded-xl border border-bridge-border bg-white px-5 py-3.5 text-left transition-all hover:-translate-y-px hover:border-bridge-accent hover:shadow-card disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
        >
          <span className="block text-[13.5px] font-bold text-bridge-fg">
            {DEFAULT_PRESET.label}
          </span>
          <span className="mt-1 block text-[12px] text-bridge-subtle">
            {DEFAULT_PRESET.summary}
          </span>
        </button>
      </FieldGroup>

      <FieldGroup
        on={grouped}
        id="g-timing"
        title="Timing"
        description="How long a viewing runs, and how often one starts."
      >
        <div className={clsx("grid grid-cols-2", grouped ? "gap-x-6 gap-y-5" : "gap-3")}>
          {timingFields.map(numericField)}
          {!grouped && timezoneField}
        </div>
      </FieldGroup>

      <FieldGroup
        on={grouped}
        id="g-window"
        title="Window"
        description="When slots may be generated, for properties with no availability yet."
      >
        {grouped && (
          <div className={clsx("mb-5 grid grid-cols-2", grouped ? "gap-x-6 gap-y-5" : "gap-3")}>
            {windowNumericFields.map(numericField)}
            {timezoneField}
          </div>
        )}

        {/* ── Default availability window ──────────────────────────────────────
            Only used when atlas auto-provisions an AVAILABILITY diary entry for
            a property that has none. Once a real entry exists, these settings
            are ignored and the slot-slicing rules take over.                  */}
        <div
          className={clsx("space-y-3", !grouped && "border-t border-bridge-border/60 pt-4")}
        >
          {!grouped && (
            <div>
              <p className="flex items-center gap-1 text-xs font-medium text-bridge-muted">
                Default availability window
                <InfoHint content="Used only when atlas auto-provisions availability for a property that has none. Once a real availability entry exists these are ignored, and the slot rules above take over." />
              </p>
              <p className="mt-0.5 text-[11px] text-bridge-subtle">
                Applied when a property has no availability set up yet. The server falls back to
                10:00–17:00, weekly Tue–Fri if left unconfigured.
              </p>
            </div>
          )}

          {/* Recurrence type */}
          <div data-preview-anchor="recurrence">
            <label className={labelClass}>
              <span>Repeat</span>
              <InfoHint content="Whether the auto-provisioned window repeats every day, or only on chosen weekdays." />
            </label>
            <div className="flex gap-1.5">
              {(["WEEKLY", "DAILY"] as const).map((t) => {
                // No fallback: an unset value highlights neither button, so a
                // blank form doesn't imply a choice the payload doesn't carry.
                const active = value.defaultRecurrenceType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange({ ...value, defaultRecurrenceType: t })}
                    className={clsx(
                      "rounded-md border px-3 py-1 text-xs font-medium transition-colors",
                      active
                        ? "border-bridge-accent bg-bridge-accent text-white"
                        : "border-bridge-border/60 bg-bridge-surface text-bridge-muted hover:text-bridge-fg",
                      disabled && "cursor-not-allowed",
                    )}
                  >
                    {t === "WEEKLY" ? "Weekly" : "Daily"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Days of week — only meaningful for WEEKLY (or before a pick is made) */}
          {value.defaultRecurrenceType !== "DAILY" && (
            <div>
              <label className={labelClass}>
                <span>Days</span>
                <InfoHint content="Weekdays the auto-provisioned window repeats on — required once Repeat is set to Weekly." />
              </label>
              {/* No Mon–Fri fallback: an unset value shows nothing selected, so
                  a blank form doesn't imply days the payload doesn't carry. */}
              <WeekdayPicker
                value={value.defaultRecurrenceDays ?? []}
                onChange={(days) => onChange({ ...value, defaultRecurrenceDays: days })}
                disabled={disabled}
              />
              <p className={hintClass}>Days the auto-provisioned window repeats on.</p>
            </div>
          )}

          {/* Window start / end */}
          <div data-preview-anchor="window" className="space-y-2">
            {/* Drag-to-set companion for the two fields below — same values,
                both directions. */}
            {interactiveWindow && (
              <TimeWindowSlider
                start={value.defaultWindowStart}
                end={value.defaultWindowEnd}
                disabled={disabled}
                onChange={({ start, end }) =>
                  onChange({ ...value, defaultWindowStart: start, defaultWindowEnd: end })
                }
              />
            )}
            <div className={clsx("grid grid-cols-2", grouped ? "gap-x-6 gap-y-5" : "gap-3")}>
              <div>
                <label
                  htmlFor="default-window-start"
                  className={labelClass}
                >
                  <span>
                    Window start
                    {value.defaultRecurrenceType != null && !windowOptional && <RequiredMark />}
                  </span>
                  <InfoHint content="Time of day the auto-provisioned window opens — the earliest a slot can start." />
                </label>
                <DiaryTimeField
                  id="default-window-start"
                  value={value.defaultWindowStart ? toLocalTime(value.defaultWindowStart) : ""}
                  onChange={(local) =>
                    onChange({ ...value, defaultWindowStart: fromLocalTime(local) })
                  }
                  disabled={disabled}
                />
                <p className={hintClass}>Earliest slot start</p>
              </div>
              <div>
                <label
                  htmlFor="default-window-end"
                  className={labelClass}
                >
                  <span>
                    Window end
                    {value.defaultRecurrenceType != null && !windowOptional && <RequiredMark />}
                  </span>
                  <InfoHint content="Time of day the window closes. No slot may start at or after this time, so the last slot ends by it." />
                </label>
                <DiaryTimeField
                  id="default-window-end"
                  value={value.defaultWindowEnd ? toLocalTime(value.defaultWindowEnd) : ""}
                  onChange={(local) =>
                    onChange({ ...value, defaultWindowEnd: fromLocalTime(local) })
                  }
                  disabled={disabled}
                />
                <p className={hintClass}>No slot may start at or after this time</p>
              </div>
            </div>
          </div>

          {/* Two different messages for two different situations: a window
              that is missing and needed, versus one that is missing and
              irrelevant because the property brought its own. */}
          {windowOptional ? (
            <div
              role="status"
              className="flex items-start gap-2 rounded-md border border-bridge-border bg-bridge-bg px-3 py-2 text-[12px] text-bridge-muted"
            >
              <Info size={14} className="mt-px shrink-0" />
              <span>
                This property already has its own availability — the blue marker on it. The window
                below is only used to create availability for a property that has none, so it is
                optional here. The slot rules above still apply.
              </span>
            </div>
          ) : (
            windowConfigured || (
              <div
                role="status"
                className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-700"
              >
                <AlertTriangle size={14} className="mt-px shrink-0" />
                <span>
                  No default availability window is set —{" "}
                  {value.defaultRecurrenceType === "WEEKLY"
                    ? "pick at least one day for the weekly repeat."
                    : "set Repeat to Daily, or Weekly with the days it runs on."}{" "}
                  You can still save these settings without it.
                </span>
              </div>
            )
          )}
        </div>
      </FieldGroup>

      <FieldGroup
        on={grouped}
        id="g-team"
        title="Team"
        description="Agents assigned to the generated slots, in priority order."
      >
        <div data-preview-anchor="users">
          <label className={labelClass}>
            <span>Default users (priority order) {usersRequired && <RequiredMark />}</span>
            <InfoHint content="Agents assigned to the generated slots. The order is the priority the first available agent is picked in — reorder with the arrows." />
          </label>

          {/*
            Opt-in. Off, the list below is a strict priority order and the first free agent takes
            every booking; on, bookings are spread least-recently-assigned first and the order is
            only the tie-break. It sits directly above the list because it decides how that list
            is read.
          */}
          <label
            className={clsx(
              "mb-2 flex items-start gap-2 rounded-lg border border-bridge-border/60 px-3 py-2",
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
            )}
          >
            <input
              type="checkbox"
              checked={value.roundRobinEnabled ?? false}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, roundRobinEnabled: e.target.checked })}
              className="mt-0.5 h-4 w-4 cursor-pointer accent-bridge-accent disabled:cursor-not-allowed"
            />
            <span>
              <span className="block text-[13px] font-semibold text-bridge-fg">
                Share viewings round-robin
              </span>
              <span className="block text-[12px] text-bridge-muted">
                Give each booking to whoever went longest without one. Off, the highest-priority
                agent below who is free takes every viewing.
              </span>
            </span>
          </label>
          <MenuSelect
            value=""
            placeholder={usersLoading ? "Loading users…" : "Add a user…"}
            searchable
            loading={usersLoading}
            disabled={disabled}
            onChange={addUser}
            onDropdownSearchQueryChange={(q) => {
              setUserSearchQuery(q);
              onUserSearchChange?.(q);
            }}
            options={userOptions}
          />
          <ol className="mt-2 space-y-1">
            {defaultUsers.map((u, idx) => (
              <li
                key={u.id}
                className="flex items-center gap-2 rounded-md border border-bridge-border/60 px-2 py-1 text-sm"
              >
                <span className="w-5 text-center text-xs font-semibold text-bridge-muted">
                  {idx + 1}
                </span>
                <span className="flex-1 truncate">{labelFor(u)}</span>
                <button
                  type="button"
                  className="text-bridge-muted hover:text-bridge-fg disabled:opacity-30"
                  onClick={() => move(idx, -1)}
                  disabled={disabled || idx === 0}
                  aria-label="Move up"
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  type="button"
                  className="text-bridge-muted hover:text-bridge-fg disabled:opacity-30"
                  onClick={() => move(idx, 1)}
                  disabled={disabled || idx === defaultUsers.length - 1}
                  aria-label="Move down"
                >
                  <ChevronDown size={16} />
                </button>
                <button
                  type="button"
                  className="text-bridge-muted hover:text-rose-500 disabled:opacity-30"
                  onClick={() => removeUser(u.id)}
                  disabled={disabled}
                  aria-label="Remove"
                >
                  <X size={16} />
                </button>
              </li>
            ))}
            {defaultUsers.length === 0 && (
              <li className="px-1 py-1 text-xs text-bridge-subtle">No users added yet.</li>
            )}
          </ol>

          {/*
            Assignments the nightly CRM roster reconciliation switched off. These used to be DELETED
            outright and never restored, so an operator's configuration quietly shrank overnight —
            which is what "the settings changed by themselves" turned out to be. They are kept and
            shown here instead; adding the person back re-activates the assignment on save.
          */}
          {inactiveAssignments.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-300/70 bg-amber-50/60 px-2 py-2">
              <p className="px-1 pb-1 text-xs font-medium text-amber-800">
                Not in the CRM roster — excluded from booking
              </p>
              <ul className="space-y-1">
                {inactiveAssignments.map((a) => (
                  <li
                    key={a.userId}
                    className="flex items-center gap-2 rounded px-1 py-1 text-sm text-bridge-muted"
                  >
                    <span className="flex-1 truncate line-through">{a.name ?? a.userId}</span>
                    {a.deactivatedAt && (
                      <span className="shrink-0 text-[11px] text-amber-700">
                        removed {formatLocalDate(a.deactivatedAt)}
                      </span>
                    )}
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-amber-800 hover:underline disabled:opacity-40"
                      onClick={() => restoreUser(a)}
                      disabled={disabled}
                    >
                      Restore
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </FieldGroup>

      {onSave &&
        (() => {
          const reasons: string[] = [];
          if (usersRequired && defaultUsers.length === 0) {
            reasons.push("Add at least one default user");
          }
          if (saveDisabled) reasons.push("No changes to save");
          const saveBlocked = reasons.length > 0;
          return (
            <div className="flex justify-end">
              <Tooltip content={reasons.join(" • ")} side="top">
                <span className={saveBlocked ? "inline-flex cursor-not-allowed" : "inline-flex"}>
                  <Button
                    variant="primary"
                    onClick={onSave}
                    loading={saving}
                    disabled={saveBlocked}
                    className={saveBlocked ? "pointer-events-none" : undefined}
                  >
                    Save settings
                  </Button>
                </span>
              </Tooltip>
            </div>
          );
        })()}
    </div>
  );
}
